import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Check,
  Copy,
  Italic,
  List,
  Loader2,
  Maximize2,
  Minimize2,
  Printer,
  Redo2,
  Undo2,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@src/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { cn } from "@src/shared/lib/utils";

export interface VisitNoteEditorProps {
  value?: string | null;
  placeholder?: string;
  ariaLabel?: string;
  minHeightClassName?: string;
  onPrint?: () => void | Promise<void>;
  printLabel?: string;
  printDisabled?: boolean;
  isPrinting?: boolean;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function noteTextToHtml(value: string): string {
  const lines = value.split(/\r?\n/);
  const html: string[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    html.push(`<ul>${listItems.join("")}</ul>`);
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      continue;
    }

    if (trimmed === "---") {
      flushList();
      html.push("<hr>");
      continue;
    }

    if (trimmed.startsWith("*")) {
      listItems.push(`<li>${escapeHtml(trimmed.replace(/^\*\s*/, ""))}</li>`);
      continue;
    }

    flushList();
    html.push(
      trimmed.endsWith(":")
        ? `<p><strong>${escapeHtml(trimmed)}</strong></p>`
        : `<p>${escapeHtml(trimmed)}</p>`,
    );
  }

  flushList();
  return html.join("");
}

function ToolbarButton({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "size-7 rounded-md text-muted-foreground hover:text-foreground",
            pressed && "bg-primary/10 text-primary hover:text-primary",
          )}
          aria-label={label}
          aria-pressed={pressed}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function VisitNoteEditor({
  value,
  placeholder = "The generated note will appear here when it is ready.",
  ariaLabel = "Visit note editor",
  minHeightClassName = "min-h-[360px]",
  onPrint,
  printLabel = "Print note",
  printDisabled,
  isPrinting = false,
}: VisitNoteEditorProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasContent, setHasContent] = useState(Boolean(value?.trim()));
  const initialContent = useMemo(
    () => (value?.trim() ? noteTextToHtml(value) : `<p>${placeholder}</p>`),
    [value, placeholder],
  );
  const editor = useEditor({
    extensions: [StarterKit],
    content: initialContent,
    immediatelyRender: false,
    onUpdate({ editor: updatedEditor }) {
      const text = updatedEditor.getText().trim();
      setHasContent(text.length > 0 && text !== placeholder);
    },
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        class: cn(
          minHeightClassName,
          "flex-1 overflow-y-auto px-4 py-3 text-[13px] leading-relaxed outline-none",
        ),
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    if (editor.getHTML() !== initialContent) {
      editor.commands.setContent(initialContent);
    }
    setHasContent(Boolean(value?.trim()));
  }, [editor, initialContent, value]);

  useEffect(() => {
    if (!expanded) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);

  const canCopy = hasContent;

  const copyNote = async () => {
    if (!editor) return;

    await navigator.clipboard.writeText(
      editor.getText({ blockSeparator: "\n" }),
    );
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const printNote = () => {
    if (!editor) return;

    const frame = document.createElement("iframe");
    frame.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) {
      frame.remove();
      return;
    }

    doc.open();
    doc.write(`<!doctype html><html><head><title>Visit note</title><style>
      body{font-family:system-ui,sans-serif;padding:24px;line-height:1.55;color:#15171a}
      p{margin:0 0 10px} ul{margin:0 0 12px;padding-left:1.4rem} hr{border:0;border-top:1px solid #dfe4e7;margin:16px 0}
    </style></head><body>${editor.getHTML()}</body></html>`);
    doc.close();
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 500);
  };
  const handlePrint = () => {
    if (onPrint) {
      void onPrint();
      return;
    }

    printNote();
  };
  const effectivePrintLabel = isPrinting ? "Generating report" : printLabel;
  const isPrintDisabled = isPrinting || (printDisabled ?? !canCopy);

  const shell = (
    <div
      className={cn(
        "flex h-full min-h-0 flex-1 flex-col rounded-[10px] border border-border bg-background shadow-none",
        expanded &&
          "fixed inset-4 z-50 rounded-xl shadow-2xl md-stitch:inset-8",
      )}
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex min-h-10 items-center justify-between gap-2 rounded-t-[10px] border-border border-b bg-surface px-2 py-1">
          <div className="flex min-w-0 items-center gap-1">
            <ToolbarButton
              label="Undo"
              disabled={!editor?.can().undo()}
              onClick={() => editor?.chain().focus().undo().run()}
            >
              <Undo2 className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Redo"
              disabled={!editor?.can().redo()}
              onClick={() => editor?.chain().focus().redo().run()}
            >
              <Redo2 className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
            <ToolbarButton
              label="Bold"
              pressed={editor?.isActive("bold")}
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              <Bold className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Italic"
              pressed={editor?.isActive("italic")}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            >
              <Italic className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Bulleted list"
              pressed={editor?.isActive("bulletList")}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <List className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <ToolbarButton
              label={copied ? "Copied" : "Copy note"}
              disabled={!canCopy}
              onClick={copyNote}
            >
              {copied ? (
                <Check className="size-3.5 text-success" aria-hidden="true" />
              ) : (
                <Copy className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
            <ToolbarButton
              label={effectivePrintLabel}
              disabled={isPrintDisabled}
              onClick={handlePrint}
            >
              {isPrinting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Printer className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
            <ToolbarButton
              label={expanded ? "Exit full screen" : "Expand note"}
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? (
                <Minimize2 className="size-3.5" aria-hidden="true" />
              ) : (
                <Maximize2 className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
          </div>
        </div>
      </TooltipProvider>

      <EditorContent
        editor={editor}
        className="flex min-h-0 flex-1 flex-col overflow-hidden [&_.ProseMirror_hr]:my-4 [&_.ProseMirror_hr]:border-border [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p]:mt-0 [&_.ProseMirror_strong]:font-semibold [&_.ProseMirror_ul]:mb-3 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5"
      />
    </div>
  );

  if (!expanded) return shell;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
        aria-hidden="true"
      />
      {shell}
    </>
  );
}
